import {
    getSuggestedRegionFromIpCountry,
    getFriendlyRegionName,
    regionDomainUrls,
    prefixWithLang,
    getSunlightenHostParts,
    getSunlightenDomainSuffix,
} from '../global/countries';
import { normalizeLocale } from './locale';
import { getIpLocation } from './ipLocation';
import Cookies from 'js-cookie';

const SESSION_KEY = 'geo_suggested';
// Before REGION_COOKIE the choice was spread over these session keys. They are
// still read so a tab that chose under the old scheme is not prompted again,
// and removed whenever a new choice is stored.
const LEGACY_SESSION_KEYS = {
    countryCode: 'geo_countryCode',
    firstVisit: 'geo_firstVisit',
    bannerDismissed: 'geo_bannerDismissed',
};

const safeSessionGet = (key: string): string | null => {
    try {
        return typeof window !== 'undefined'
            ? sessionStorage.getItem(key)
            : null;
    } catch {
        return null;
    }
};

const safeSessionSet = (key: string, value: string): void => {
    try {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(key, value);
        }
    } catch {
        // ignore storage errors (e.g. private browsing)
    }
};

const safeSessionRemove = (key: string): void => {
    try {
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem(key);
        }
    } catch {
        // ignore storage errors (e.g. private browsing)
    }
};

/**
 * The visitor's region choice outlives the tab: a first-party cookie per
 * site. It holds the chosen region code, or REGION_STAYED when they dismissed
 * the prompt. The same value is mirrored in sessionStorage under the same name
 * for frames where the cookie can't be written (e.g. the Kontent preview).
 * Cookies can't cross domains, so moving between .com/.com.au/.co.nz still
 * hands the choice over with `?cc=` (CountrySelectOverlay), which then sets
 * this cookie on the destination site.
 */
export const REGION_COOKIE = 'sl_region';
export const REGION_STAYED = 'stay';
const REGION_COOKIE_MAX_AGE_DAYS = 365;

/** `.sunlighten.com.au` for www/apex production hosts; host-only otherwise. */
const regionCookieDomain = (host: string): string | undefined => {
    if (!PRODUCTION_SUNLIGHTEN_HOSTS.has(host.toLowerCase())) return undefined;
    return `.sunlighten${getSunlightenHostParts(host).suffix}`;
};

export const readRegionCookie = (): string | null => {
    try {
        if (typeof document === 'undefined') return null;
        return Cookies.get(REGION_COOKIE) ?? null;
    } catch {
        return null;
    }
};

const writeRegionCookie = (value: string): void => {
    try {
        if (typeof document === 'undefined') return;
        const { host, protocol } = window.location;
        Cookies.set(REGION_COOKIE, value, {
            expires: REGION_COOKIE_MAX_AGE_DAYS,
            path: '/',
            sameSite: 'lax',
            domain: regionCookieDomain(host),
            secure: protocol === 'https:',
        });
    } catch {
        // ignore cookie errors (e.g. sandboxed frames)
    }
};

/** The stored choice: a region code, REGION_STAYED, or null if none yet. */
const readRegionChoice = (): string | null =>
    readRegionCookie() ||
    safeSessionGet(REGION_COOKIE) ||
    safeSessionGet(LEGACY_SESSION_KEYS.countryCode);

export const getGeoCountryCode = (): string | null => {
    const choice = readRegionChoice();
    return choice && choice !== REGION_STAYED ? choice : null;
};

export const markGeoRegionChosen = (regionCode?: string): void => {
    const choice = regionCode
        ? (regionCode === 'default' ? 'us-en' : regionCode).toLowerCase()
        : REGION_STAYED;
    writeRegionCookie(choice);
    safeSessionSet(REGION_COOKIE, choice);
    Object.values(LEGACY_SESSION_KEYS).forEach(safeSessionRemove);
    clearGeoSessionCache();
};

export const clearGeoSessionCache = (): void => safeSessionRemove(SESSION_KEY);

export const hasUserAlreadyChosenRegion = (): boolean =>
    !!readRegionChoice() ||
    safeSessionGet(LEGACY_SESSION_KEYS.bannerDismissed) === 'true' ||
    safeSessionGet(LEGACY_SESSION_KEYS.firstVisit) === 'false';

export async function detectSuggestedRegion(preferredLanguage?: string) {
    try {
        if (typeof window === 'undefined') return null;

        const normalizedPreferredLanguage = normalizeLocale(preferredLanguage);

        if (
            isLocalhost(window.location.host) &&
            process.env.GATSBY_GEO_DEBUG_REGION
        ) {
            const debugRegion = normalizeLocale(
                process.env.GATSBY_GEO_DEBUG_REGION
            );

            return {
                suggestedCode: debugRegion,
                suggestedName: getFriendlyRegionName(debugRegion),
            };
        }

        const cachedSuggestion = safeSessionGet(SESSION_KEY);
        if (cachedSuggestion) {
            try {
                const cachedResult = JSON.parse(cachedSuggestion);
                if (!cachedResult || !cachedResult.suggestedCode) {
                    clearGeoSessionCache();
                } else {
                    const cachedRegionCode = normalizeLocale(
                        cachedResult.suggestedCode
                    );
                    if (cachedRegionCode === normalizedPreferredLanguage) {
                        return null;
                    }
                    return {
                        suggestedCode: cachedRegionCode,
                        suggestedName: cachedResult.suggestedName,
                    };
                }
            } catch {
                clearGeoSessionCache();
            }
        }

        const ipDetails = await getIpLocation();
        const ipCountryCode = (ipDetails?.country_code || '').toLowerCase();

        if (!ipCountryCode) return null;

        const suggestedRegionRaw = getSuggestedRegionFromIpCountry(
            ipCountryCode,
            preferredLanguage
        );

        const suggestedRegionCode = normalizeLocale(suggestedRegionRaw);

        if (
            !suggestedRegionCode ||
            suggestedRegionCode === normalizedPreferredLanguage
        ) {
            return null;
        }

        const suggestedRegionName = getFriendlyRegionName(suggestedRegionCode);
        const suggestion = {
            suggestedCode: suggestedRegionCode,
            suggestedName: suggestedRegionName,
        };

        try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(suggestion));
        } catch {
            // ignore storage errors
        }

        return suggestion;
    } catch (err) {
        console.error('[geo] detectSuggestedRegion error', err);
        return null;
    }
}

export const isLocalhost = (host?: string): boolean => {
    if (!host && typeof window !== 'undefined') {
        host = window.location.host;
    }

    if (!host) return false;

    const hostLower = host.toLowerCase();

    return hostLower.includes('localhost') || hostLower.startsWith('127.');
};

export const isVercelDomain = (host?: string): boolean => {
    if (!host && typeof window !== 'undefined') {
        host = window.location.host;
    }

    if (!host) return false;

    return host.toLowerCase().endsWith('.vercel.app');
};

export const isSunlightenDomain = (host?: string): boolean => {
    if (!host && typeof window !== 'undefined') {
        host = window.location.host;
    }

    if (!host) return false;

    return host.toLowerCase().includes('sunlighten');
};

const normalizePath = (p: string): string => {
    if (!p) return '/';
    return p.startsWith('/') ? p : `/${p}`;
};

const withOrigin = (path: string): string => {
    if (typeof window === 'undefined') return path || '/';
    return `${window.location.origin}${path || '/'}`;
};

const VERCEL_SUBDOMAIN_LABELS: Record<string, string> = {
    'au-en': 'australia',
    'nz-en': 'newzealand',
};

function composeVercelUrl(
    region: string,
    path: string,
    host: string,
    protocol: string,
    regionPath: string
): string {
    const vercelLabel = VERCEL_SUBDOMAIN_LABELS[region];
    if (!vercelLabel) {
        return `${protocol}//${host}${regionPath}`;
    }

    const hostSegments = host.split('.');
    const hostPrefix = hostSegments[0];
    const hostSuffix = hostSegments.slice(1).join('.');
    const prefixParts = hostPrefix.split('-');

    if (prefixParts.includes(vercelLabel)) {
        return `${protocol}//${host}${path}`;
    }

    const newPrefix = [
        prefixParts[0],
        vercelLabel,
        ...prefixParts.slice(1),
    ].join('-');

    return `${protocol}//${newPrefix}.${hostSuffix}${path}`;
}

const PRODUCTION_SUNLIGHTEN_HOSTS = new Set([
    'www.sunlighten.com',
    'sunlighten.com',
    'www.sunlighten.com.au',
    'sunlighten.com.au',
    'www.sunlighten.co.nz',
    'sunlighten.co.nz',
]);

function isProductionSunlightenHost(host: string): boolean {
    return PRODUCTION_SUNLIGHTEN_HOSTS.has(host.toLowerCase());
}

function composeProductionSunlightenUrl(
    region: string,
    path: string,
    protocol: string,
    regionPath: string
): string {
    const targetDomainUrl = regionDomainUrls[region];
    const defaultDomainUrl = regionDomainUrls['default'];

    if (targetDomainUrl && targetDomainUrl !== defaultDomainUrl) {
        const targetHostname = new URL(targetDomainUrl).hostname;
        return `${protocol}//${targetHostname}${path}`;
    }

    const defaultHostname = new URL(defaultDomainUrl).hostname;
    return `${protocol}//${defaultHostname}${regionPath}`;
}

function composeNonProductionSunlightenUrl(
    region: string,
    path: string,
    host: string,
    protocol: string,
    regionPath: string
): string {
    const { prefix, suffix: currentSuffix } = getSunlightenHostParts(host);

    const targetDomainUrl =
        regionDomainUrls[region] || regionDomainUrls['default'];
    const defaultDomainUrl = regionDomainUrls['default'];
    const targetSuffix = getSunlightenDomainSuffix(targetDomainUrl);

    if (currentSuffix.toLowerCase() !== targetSuffix.toLowerCase()) {
        const newHost = `${prefix}sunlighten${targetSuffix}`;
        const finalPath =
            targetDomainUrl !== defaultDomainUrl ? path : regionPath;
        return `${protocol}//${newHost}${finalPath}`;
    }

    return `${protocol}//${host}${regionPath}`;
}

function composeSunlightenUrl(
    region: string,
    path: string,
    host: string,
    protocol: string,
    regionPath: string
): string {
    if (!host.toLowerCase().includes('sunlighten')) {
        return `${protocol}//${host}${regionPath}`;
    }

    if (isProductionSunlightenHost(host)) {
        return composeProductionSunlightenUrl(
            region,
            path,
            protocol,
            regionPath
        );
    }

    return composeNonProductionSunlightenUrl(
        region,
        path,
        host,
        protocol,
        regionPath
    );
}

export function composeRegionRedirectUrl(
    regionCode: string,
    currentPath: string
): string {
    let region = (regionCode || '').toLowerCase();

    if (region === 'us-en') {
        region = 'default';
    }

    const path = normalizePath(currentPath);

    if (typeof window === 'undefined') {
        if (region === 'default') return path;
        return `/${region}${path}`;
    }

    const { protocol, host, origin } = window.location;
    const regionPath = prefixWithLang(path, region);

    if (isLocalhost(host)) return `${origin}${regionPath}`;
    if (isVercelDomain(host))
        return composeVercelUrl(region, path, host, protocol, regionPath);
    if (isSunlightenDomain(host))
        return composeSunlightenUrl(region, path, host, protocol, regionPath);

    return `${origin}${regionPath}`;
}

export function replaceRegionPlaceholders(
    html: string,
    currentRegionLabel: string,
    detectedRegionLabel: string
) {
    if (!html) return html;
    return html
        .replace(/\{current_region\}/gi, currentRegionLabel)
        .replace(/\{user_region\}/gi, detectedRegionLabel);
}
